"use client";

import { Building2, Calendar, DollarSign, MapPin, ShoppingCart } from "lucide-react";

// ─── Local Types ──────────────────────────────────────

export type SearchContext = "spend" | "map";
export type FilterToken = { key: string; value: string };

// ─── Suggestion Config ────────────────────────────────────

export type FilterSuggestion = {
  key: string;
  value: string;
  description: string;
  icon: React.ReactNode;
};

/** Filter suggestions shown when the search context is "spend". */
export const SPEND_SUGGESTIONS: FilterSuggestion[] = [
  {
    key: "year",
    value: "2025",
    description: "object type",
    icon: <Calendar className="h-4 w-4" />,
  },
  {
    key: "amount",
    value: ">1000",
    description: "order amount greater than",
    icon: <DollarSign className="h-4 w-4" />,
  },
  {
    key: "amount",
    value: "<1000",
    description: "order amount less than",
    icon: <DollarSign className="h-4 w-4" />,
  },
];

/** Filter suggestions shown when the search context is "map". */
export const MAP_SUGGESTIONS: FilterSuggestion[] = [
  {
    key: "city",
    value: "delft",
    description: "city",
    icon: <Building2 className="h-4 w-4" />,
  },
  {
    key: "radius",
    value: "10km",
    description: "search radius",
    icon: <MapPin className="h-4 w-4" />,
  },
  {
    key: "has",
    value: "orders",
    description: "has orders",
    icon: <ShoppingCart className="h-4 w-4" />,
  },
];

/** All suggestions combined — used for icon lookups on active tokens. */
export const ALL_SUGGESTIONS = [...SPEND_SUGGESTIONS, ...MAP_SUGGESTIONS];

/**
 * Returns the correct suggestion list for a search context.
 */
export function getSuggestionsForContext(): FilterSuggestion[] {
  return MAP_SUGGESTIONS;
}

/**
 * Filters suggestions based on current input state.
 */
export function getFilteredSuggestions(searchText: string, draftFilterKey: string | null): FilterSuggestion[] {
  const suggestions = getSuggestionsForContext();

  if (draftFilterKey) {
    return suggestions.filter((s) => s.key === draftFilterKey && s.value.toLowerCase().includes(searchText.toLowerCase()));
  }

  if (searchText) {
    const lower = searchText.toLowerCase();
    return suggestions.filter((s) => s.key.toLowerCase().startsWith(lower) || s.value.toLowerCase().includes(lower) || `${s.key}:${s.value}`.toLowerCase().includes(lower));
  }

  return suggestions;
}

/**
 * Find the icon for a given filter token (if it matches a known suggestion).
 */
export function getTokenIcon(token: FilterToken): React.ReactNode | undefined {
  const match = ALL_SUGGESTIONS.find((s) => s.key === token.key && s.value === token.value);
  return match?.icon;
}
